import React, { useMemo, useState } from 'react';

import { ReactComponent as DeleteIcon } from 'assets/icons/deleteIcon.svg';
import { ReactComponent as PlusIcon } from 'assets/pics/plus.svg';
import { useFormik } from 'formik';
import { useCurrentExperts } from 'hooks/UseCurrentExperts';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { ThemedTextField } from 'components/ThemedTextField/ThemedTextField';

import { VALIDATION_CONSTANTS } from 'global/constants';

import { EventExpertsMultiComboBox } from './EventExpertsMultiComboBox';
import { EventRegisterParticipantItem } from './EventRegisterParticipantItem';
import { EventRegisterRemoveParticipantModal } from './EventRegisterRemoveParticipantModal';
import { DUPLICATE_PARTICIPANT_EMAIL } from './YEventRegistration';
import { createGuestParticipantId, getExpertParticipantId } from './eventParticipantId.utils';

export const EventRegisterParticipantsSection = ({
  canGuestRegister,
  disableMaxParticipation,
  maxParticipants,
  participants,
  handleBlur,
  formik,
  isRegistrationExpired,
  iamType,
  editingParticipantEmailIds,
  setParticipantEmailEditing,
  onRemoveParticipant,
}) => {
  const isEiamUser = iamType === 'EIAM';
  const { experts, isLoading: isExpertsLoading } = useCurrentExperts({ skip: isEiamUser });
  const [isGuestRowVisible, setIsGuestRowVisible] = useState(false);
  const [activeParticipantEmailIndex, setActiveParticipantEmailIndex] = useState(null);
  const [participantPendingRemoval, setParticipantPendingRemoval] = useState(null);
  const canAddParticipants = disableMaxParticipation || participants.length < maxParticipants;
  const isParticipantsActionDisabled = isRegistrationExpired || !canAddParticipants;
  const setParticipants = (newParticipants) =>
    formik.setFieldValue('participants', newParticipants, true);

  const guestValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        guestName: Yup.string()
          .trim()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .required(VALIDATION_CONSTANTS.MANDATORY),
        guestEmail: Yup.string()
          .trim()
          .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .test('unique-guest-email', DUPLICATE_PARTICIPANT_EMAIL, (value) => {
            if (!value?.trim()) {
              return true;
            }

            const normalizedEmail = value.trim().toLowerCase();
            return !participants.some(
              (participant) => (participant?.eMail || '').trim().toLowerCase() === normalizedEmail
            );
          }),
      }),
    [participants]
  );

  const guestFormik = useFormik({
    initialValues: {
      guestName: '',
      guestEmail: '',
    },
    validateOnBlur: formik.validateOnBlur,
    validateOnChange: formik.validateOnChange,
    validateOnMount: formik.validateOnMount,
    validationSchema: guestValidationSchema,
    onSubmit: ({ guestName, guestEmail }, { resetForm }) => {
      if (!canAddParticipants) return;

      const normalizedName = guestName.trim().replace(/\s+/g, ' ');
      const [firstName, ...lastNameParts] = normalizedName.split(' ');
      const lastName = lastNameParts.join(' ') || firstName;

      setParticipants([
        ...participants,
        {
          firstName,
          lastName,
          id: createGuestParticipantId(),
          fachkraftId: null,
          eMail: guestEmail.trim(),
          netzPortalEmail: null,
        },
      ]);

      resetForm();
      setIsGuestRowVisible(false);
    },
  });

  const expertParticipants = useMemo(
    () => participants.filter((participant) => Boolean(participant?.fachkraftId)),
    [participants]
  );

  const selectedExpertIds = useMemo(() => {
    if (!experts?.length) return [];

    const availableExpertIds = new Set(experts.map((expert) => String(expert.id)));

    return expertParticipants
      .map((participant) => participant.fachkraftId)
      .filter((fachkraftId) => availableExpertIds.has(String(fachkraftId)));
  }, [expertParticipants, experts]);

  const guestParticipants = useMemo(
    () => participants.filter((participant) => !participant?.fachkraftId),
    [participants]
  );
  const maxExpertSelections = disableMaxParticipation
    ? undefined
    : Math.max(maxParticipants - guestParticipants.length, 0);

  const removeParticipant = (event, index) => {
    event.preventDefault();
    setParticipantPendingRemoval({ index, participant: participants[index] });
  };

  const closeRemoveParticipantModal = () => {
    setParticipantPendingRemoval(null);
  };

  const confirmRemoveParticipant = () => {
    if (!participantPendingRemoval) {
      return;
    }

    const { index } = participantPendingRemoval;
    onRemoveParticipant(index);
    setParticipantPendingRemoval(null);

    if (activeParticipantEmailIndex === index) {
      setActiveParticipantEmailIndex(null);
    } else if (activeParticipantEmailIndex > index) {
      setActiveParticipantEmailIndex(activeParticipantEmailIndex - 1);
    }
  };

  const expertsOptions = useMemo(() => {
    if (!experts) return [];

    const selectedExpertIdsSet = new Set(selectedExpertIds.map(String));

    return experts
      .map((expert) => ({
        title: `${expert.vorname} ${expert.nachname}`,
        value: expert.id,
      }))
      .filter((expert) => !selectedExpertIdsSet.has(String(expert.value)));
  }, [experts, selectedExpertIds]);

  // The combo box only offers experts that are not part of the list yet, so a confirmed selection
  // adds to the current participants instead of replacing them. Removing happens per row.
  const onExpertsChange = (_, expertIds) => {
    if (!Array.isArray(expertIds) || !experts) {
      return;
    }

    const alreadySelectedExpertIds = new Set(
      expertParticipants.map((participant) => String(participant.fachkraftId))
    );

    const addedExperts = expertIds
      .filter((expertId) => !alreadySelectedExpertIds.has(String(expertId)))
      .map((expertId) => experts.find((expert) => String(expert.id) === String(expertId)))
      .filter(Boolean)
      .map((expert) => ({
        id: getExpertParticipantId(expert.id),
        firstName: expert?.vorname || expert?.firstName || '',
        lastName: expert?.nachname || expert?.lastName || '',
        eMail: expert?.netzportalEmail || expert?.eMail || '',
        netzPortalEmail: expert?.netzportalEmail || '',
        fachkraftId: expert.id,
      }));

    if (!addedExperts.length) {
      return;
    }

    const freeExpertSlots =
      typeof maxExpertSelections === 'number'
        ? Math.max(maxExpertSelections - alreadySelectedExpertIds.size, 0)
        : addedExperts.length;

    setParticipants([...participants, ...addedExperts.slice(0, freeExpertSlots)]);
  };

  const resetGuestRow = () => {
    guestFormik.resetForm();
    setIsGuestRowVisible(false);
  };

  const addGuestParticipant = (event) => {
    event.preventDefault();
    guestFormik.submitForm();
  };

  let participantsControlContent;
  if (isEiamUser) {
    participantsControlContent = null;
  } else if (isExpertsLoading || !experts) {
    participantsControlContent = <LoadingIndicator />;
  } else if (isGuestRowVisible) {
    participantsControlContent = (
      <div className="event-register-form__participant-controls align-flex-center-vertical">
        <div className="event-register-form__participant-controls-field">
          <ThemedTextField
            fullWidth
            id="guestName"
            name="guestName"
            label="Name*"
            variant="standard"
            value={guestFormik.values.guestName}
            onChange={guestFormik.handleChange}
            onBlur={guestFormik.handleBlur}
            error={guestFormik.touched.guestName && Boolean(guestFormik.errors.guestName)}
            helperText={guestFormik.touched.guestName && guestFormik.errors.guestName}
          />
        </div>
        <div className="event-register-form__participant-controls-field">
          <ThemedTextField
            fullWidth
            id="guestEmail"
            name="guestEmail"
            label="E-Mail-Adresse*"
            variant="standard"
            value={guestFormik.values.guestEmail}
            onChange={guestFormik.handleChange}
            onBlur={guestFormik.handleBlur}
            inputProps={{ autoComplete: 'event-guest-email' }}
            error={guestFormik.touched.guestEmail && Boolean(guestFormik.errors.guestEmail)}
            helperText={guestFormik.touched.guestEmail && guestFormik.errors.guestEmail}
          />
        </div>

        <button
          type="button"
          className="event-register-form__participant-action-surface"
          onClick={addGuestParticipant}
          disabled={isParticipantsActionDisabled || guestFormik.isSubmitting}>
          Hinzufügen
        </button>
        <button
          type="button"
          className="event-register-form__participant-control-icon"
          onClick={resetGuestRow}>
          <DeleteIcon />
        </button>
      </div>
    );
  } else {
    participantsControlContent = (
      <div className="event-register-form__participant-controls align-flex-center-vertical">
        <div className="event-register-form__participant-controls-field">
          <EventExpertsMultiComboBox
            options={expertsOptions}
            label="Weitere Fachkraft auswählen"
            name="participants"
            value={selectedExpertIds}
            onChange={onExpertsChange}
            onBlur={handleBlur}
            disabled={isParticipantsActionDisabled}
            maxSelections={maxExpertSelections}
          />
        </div>
        {canGuestRegister && (
          <div className="event-register-form__participant-controls-field">
            <button
              type="button"
              className="event-register-form__participant-action-surface event-register-form__add-participant-btn"
              onClick={() => setIsGuestRowVisible(true)}
              disabled={isParticipantsActionDisabled}>
              <PlusIcon alt="Plus Icon" className="event-register-form__add-participant-btn-icon" />
              <span>
                {guestParticipants.length > 0 ? 'Weiteren Gast hinzufügen' : 'Gast hinzufügen'}
              </span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <EventRegisterRemoveParticipantModal
        participant={participantPendingRemoval?.participant}
        isOpen={Boolean(participantPendingRemoval)}
        isRemoving={false}
        onConfirm={confirmRemoveParticipant}
        onClose={closeRemoveParticipantModal}
      />

      <div className="event-register-form__participant-title-wrapper">
        <PageTitle
          text={`Teilnehmer ${disableMaxParticipation ? '' : `(max ${maxParticipants})`}`}
          className="event-register-form__participation-title"
          notFramed
        />
      </div>

      {participants.map((participant, index) => (
        <div key={participant.id}>
          <EventRegisterParticipantItem
            formik={formik}
            index={index}
            participant={participant}
            removeParticipant={removeParticipant}
            iamType={iamType}
            activeParticipantEmailIndex={activeParticipantEmailIndex}
            setActiveParticipantEmailIndex={setActiveParticipantEmailIndex}
            isEditingEmail={editingParticipantEmailIds.has(participant.id)}
            setParticipantEmailEditing={setParticipantEmailEditing}
            participantId={participant.id}
          />
        </div>
      ))}
      {participantsControlContent}
    </>
  );
};

EventRegisterParticipantsSection.propTypes = {
  disableMaxParticipation: PropTypes.bool.isRequired,
  maxParticipants: PropTypes.number.isRequired,
  participants: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleBlur: PropTypes.func.isRequired,
  isRegistrationExpired: PropTypes.bool.isRequired,
  formik: PropTypes.object.isRequired,
  canGuestRegister: PropTypes.bool.isRequired,
  iamType: PropTypes.string.isRequired,
  editingParticipantEmailIds: PropTypes.instanceOf(Set).isRequired,
  setParticipantEmailEditing: PropTypes.func.isRequired,
  onRemoveParticipant: PropTypes.func.isRequired,
};
